import { LucideBell, LucideTarget } from "lucide-react";
import React, { useState } from "react";
import Button from "../Button";
import SetWaterReminderModal from "../modals/water/SetWaterReminderModal";
import SetSleepReminderModal from "../modals/sleep/SetSleepReminderModal";
import SetStepsReminderModal from "../modals/steps/SetStepsReminderModal";
import SetFatigueReminderModal from "../modals/fatigue/SetFatigueReminderModal";
import SetDietReminderModal from "../modals/diet/SetDietReminderModal";
import SetMedicationReminderModal from "../modals/medication/SetMedicationReminderModal";
import SetExerciseReminderModal from "../modals/exercise/SetExerciseReminderModal";
import { useAppSelector } from "src/redux/hooks";
import { hasExistingRemindersByLogKey } from "src/helpers/reminderHelpers";

interface InitialRemindersProps {
  type?: any; // medication-logs, exercise-logs, menstruation-logs
  isSetGoals?: boolean;
  isSetReminder?: boolean;
}

const InitialReminders: React.FC<InitialRemindersProps> = ({
  type,
  isSetGoals = true,
  isSetReminder = true,
}) => {
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showGoalModal, setShowGoalModal] = useState(false);

  const { reminder } = useAppSelector((state) => state.reminder);

  // Check if there are existing reminders for this log type
  const hasExistingReminders = hasExistingRemindersByLogKey(
    reminder?.data?.reminders,
    type
  );

  const renderReminderModal = () => {
    if (type === "water-logs") {
      return (
        <SetWaterReminderModal
          show={showReminderModal}
          handleClose={() => setShowReminderModal(false)}
        />
      );
    } else if (type === "sleep-logs") {
      return (
        <SetSleepReminderModal
          show={showReminderModal}
          handleClose={() => setShowReminderModal(false)}
        />
      );
    } else if (type === "steps-logs") {
      return (
        <SetStepsReminderModal
          show={showReminderModal}
          handleClose={() => setShowReminderModal(false)}
        />
      );
    } else if (type === "fatigue-logs") {
      return (
        <SetFatigueReminderModal
          show={showReminderModal}
          handleClose={() => setShowReminderModal(false)}
        />
      );
    } else if (type === "diet-logs") {
      return (
        <SetDietReminderModal
          show={showReminderModal}
          handleClose={() => setShowReminderModal(false)}
        />
      );
    } else if (type === "medication-logs") {
      return (
        <SetMedicationReminderModal
          show={showReminderModal}
          handleClose={() => setShowReminderModal(false)}
        />
      );
    } else if (type === "exercise-logs") {
      return (
        <SetExerciseReminderModal
          show={showReminderModal}
          handleClose={() => setShowReminderModal(false)}
        />
      );
    }
    return null;
  };

  //   const renderGoalModal = () => {
  //     if (type === "water-logs") {
  //       return <SetWaterGoalModal
  //         show={showGoalModal}
  //         handleClose={() => setShowGoalModal(false)}
  //       />
  //   };

  const renderGoalText = () => {
    if (type === "water-logs") {
      return {
        title: "Set a goal for water",
        description: "Set a goal for water",
      };
    } else if (type === "sleep-logs") {
      return {
        title: "Set a goal for sleep",
        description: "Set a goal for sleep",
      };
    } else if (type === "steps-logs") {
      return {
        title: "Set a goal for steps",
        description: "Set a goal for steps",
      };
    } else if (type === "fatigue-logs") {
      return {
        title: "Set a goal for fatigue",
        description: "Set a goal for fatigue",
      };
    } else if (type === "diet-logs") {
      return {
        title: "Set a goal for diet",
        description: "Set a goal for diet",
      };
    } else if (type === "medication-logs") {
      return {
        title: "Set a goal for medication",
        description: "Set a goal for medication",
      };
    } else if (type === "exercise-logs") {
      return {
        title: "Set a goal for exercise",
        description: "Set a goal for exercise",
      };
    }
    return {
      title: "",
      description: "",
    };
  };

  const renderReminderText = () => {
    if (type === "water-logs") {
      return {
        title: "Set a reminder for water",
        description: "Set a reminder for water",
      };
    } else if (type === "sleep-logs") {
      return {
        title: "Set a reminder for sleep",
        description: "Set a reminder for sleep",
      };
    } else if (type === "steps-logs") {
      return {
        title: "Set a reminder for steps",
        description: "Set a reminder for steps",
      };
    } else if (type === "fatigue-logs") {
      return {
        title: "Set a reminder for fatigue",
        description: "Set a reminder for fatigue",
      };
    } else if (type === "diet-logs") {
      return {
        title: "Set a reminder for diet",
        description: "Set a reminder for diet",
      };
    } else if (type === "medication-logs") {
      return {
        title: "Set a reminder for medication",
        description: "Set a reminder for medication",
      };
    } else if (type === "exercise-logs") {
      return {
        title: "Set a reminder for exercise",
        description: "Set a reminder for exercise",
      };
    }
    return {
      title: "",
      description: "",
    };
  };

  return (
    <div className="flex flex-col gap-2">
      {isSetGoals && (
        <div className="flex flex-row items-center justify-between py-4 rounded-md overflow-hidden">
          <div className="flex flex-row items-center gap-2">
            <div className="bg-[var(--icon-bg-primary)] rounded-full p-2">
              <LucideTarget className="w-5 h-5 text-[var(--app-primary)]" />
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-medium">
                {renderGoalText().title}
              </span>
              <span className="text-xs text-gray-500">
                {renderGoalText().description}
              </span>
            </div>
          </div>
          <div className="flex flex-row items-center gap-2">
            <Button
              size="sm"
              variant="outlined"
              rounded="full"
              onClick={() => setShowGoalModal(true)}
            >
              Set Now
            </Button>
          </div>
        </div>
      )}

      {isSetReminder && !hasExistingReminders && (
        <div className="flex flex-row items-center justify-between py-4 rounded-md overflow-hidden">
          <div className="flex flex-row items-center gap-2">
            <div className="bg-[var(--icon-bg-primary)] rounded-full p-2">
              <LucideBell className="w-5 h-5 text-[var(--app-primary)]" />
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-medium">
                {renderReminderText().title}
              </span>
              <span className="text-xs text-gray-500">
                {renderReminderText().description}
              </span>
            </div>
          </div>
          <div className="flex flex-row items-center gap-2">
            <Button
              size="sm"
              variant="outlined"
              rounded="full"
              onClick={() => setShowReminderModal(true)}
            >
              Set Now
            </Button>
          </div>
        </div>
      )}
      {renderReminderModal()}
    </div>
  );
};

export default InitialReminders;
